import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import ScoreTrendChart from '../../components/ScoreTrendChart';
import RuleTriggerList from '../../components/RuleTriggerList';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import SiteContextForm from '../../components/SiteContextForm';
import PatrolPlanEditor from '../../components/PatrolPlanEditor';
import PatrolPlanCard from '../../components/PatrolPlanCard';
import VerificationGrid from '../../components/VerificationGrid';
import { clientApi } from '../../services/client';
import type { PatrolServicePlan, ServiceCoverage } from '../../types';
import { scoringApi } from '../../services/scoring';
import type { VendorInfo } from '../../services/client';
import extractApiError from '../../utils/extractApiError';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const statusVariant = (s: string) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'error';


interface ContextSuggestion {
  id: string;
  suggestion_text: string;
  created_at: string;
  suggested_by_user_email: string | null;
  suggested_by_vendor_name: string | null;
}


/**
 * Card surfacing any pending Site-Context-change suggestions that the
 * active vendor has submitted.  Clients can Mark as applied (close
 * the row) or Dismiss (close the row).  Either action moves the
 * suggestion out of the pending bucket so the card disappears once
 * everything's been triaged.
 */
function ContextSuggestionsCard({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<ContextSuggestion[]>({
    queryKey: ['client', 'site', siteId, 'context-suggestions'],
    queryFn: () => clientApi.getContextSuggestions(siteId),
  });
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'applied' | 'dismissed' }) =>
      clientApi.decideContextSuggestion(siteId, id, action),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', 'site', siteId, 'context-suggestions'] });
    },
  });

  // Hide silently while the initial fetch is in-flight; also hide on a
  // hard error so a network blip doesn't show a half-broken card.
  // Empty result hides the card entirely (no pending suggestions).
  if (isLoading || isError) return null;
  if (!data || data.length === 0) return null;

  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <span aria-hidden>&#9888;</span>
        Pending suggestion{data.length > 1 ? 's' : ''} from your vendor
      </div>
      <p className="mt-1 text-xs text-amber-700">
        Your vendor proposed a change to this site's context. Review and either
        apply it by editing the context fields below, or dismiss.
      </p>
      <div className="mt-3 space-y-3">
        {data.map((s) => (
          <div key={s.id} className="rounded-xl border border-amber-300 bg-white p-3">
            <div className="text-xs text-slate-500 mb-1">
              {s.suggested_by_vendor_name || 'Vendor'} &middot;{' '}
              {new Date(s.created_at).toLocaleDateString()}
            </div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap">
              {s.suggestion_text}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => decide.mutate({ id: s.id, action: 'applied' })}
                disabled={decide.isPending}
                className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:bg-emerald-600"
              >
                Mark as applied
              </button>
              <button
                onClick={() => decide.mutate({ id: s.id, action: 'dismissed' })}
                disabled={decide.isPending}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50 hover:bg-slate-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders the mobile-patrol obligation section for a site.  Only shown
 * when the site's context says service_type='mobile_patrol'.  Materializes
 * the ServiceCoverage on first edit, then loads/creates/edits the plan.
 */
function ClientPatrolPlanSection({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: coverage } = useQuery<ServiceCoverage | null>({
    queryKey: ['client', 'site', siteId, 'mobile-patrol-coverage'],
    queryFn: async () => {
      const r = await clientApi.getServiceCoverages({
        site: siteId, service_type: 'mobile_patrol', is_active: 'true',
      });
      return r.data.results?.[0] ?? null;
    },
  });

  const plan: PatrolServicePlan | null = coverage?.patrol_plan ?? null;

  const ensureAndEdit = useMutation({
    mutationFn: () => clientApi.ensureMobilePatrolCoverage(siteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', 'site', siteId, 'mobile-patrol-coverage'] });
      setEditing(true);
      setError(null);
    },
    onError: (err: unknown) => setError(extractApiError(err)),
  });

  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Patrol Plan</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Defines the visits your vendor is expected to make.
            {' '}The scoring engine will compare each week's DAR events
            against this plan.
          </p>
        </div>
        {!editing && plan && (
          <button
            onClick={() => setEditing(true)}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Edit
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {!editing && !plan && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
          <p className="text-sm text-blue-800 mb-2">
            No patrol plan yet. Create one to tell the scoring engine
            what visits you expect.
          </p>
          <button
            onClick={() => ensureAndEdit.mutate()}
            disabled={ensureAndEdit.isPending}
            className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {ensureAndEdit.isPending ? 'Preparing...' : 'Create Patrol Plan'}
          </button>
        </div>
      )}

      {!editing && plan && <PatrolPlanCard plan={plan} />}

      {editing && (
        <PatrolPlanEditor
          plan={plan}
          coverageId={coverage?.id}
          onSaved={() => {
            setEditing(false);
            qc.invalidateQueries({ queryKey: ['client', 'site', siteId, 'mobile-patrol-coverage'] });
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}


/**
 * Verification grid for the last complete scoring week.  Sprint 3.
 * Runs the same coverage-lookup query as ClientPatrolPlanSection so
 * we can render the plan's required_visits alongside their verdicts.
 */
function ClientVerificationGridSection({ siteId }: { siteId: string }) {
  // Last complete week: previous Monday..Sunday.  Server-side
  // run_scoring_cycle uses timezone.localdate() (server tz), and the
  // deployed server is UTC — so we compute the Monday in UTC too.  A
  // pure-local getDay()/getDate() approach would silently produce a
  // different week for far-west-of-UTC viewers between UTC-midnight
  // and their local midnight.
  const now = new Date();
  const todayUtc = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
  ));
  const dow = todayUtc.getUTCDay();          // 0=Sun..6=Sat
  const daysSinceMonday = (dow + 6) % 7;     // 0=Mon..6=Sun
  const lastMonday = new Date(todayUtc);
  lastMonday.setUTCDate(todayUtc.getUTCDate() - daysSinceMonday - 7);
  const lastSunday = new Date(lastMonday);
  lastSunday.setUTCDate(lastMonday.getUTCDate() + 6);
  const weekStart = lastMonday.toISOString().slice(0, 10);
  const weekEnd = lastSunday.toISOString().slice(0, 10);

  const { data: coverage } = useQuery<ServiceCoverage | null>({
    queryKey: ['client', 'site', siteId, 'mobile-patrol-coverage'],
    queryFn: async () => {
      const r = await clientApi.getServiceCoverages({
        site: siteId, service_type: 'mobile_patrol', is_active: 'true',
      });
      return r.data.results?.[0] ?? null;
    },
  });

  const { data: verifData } = useQuery({
    queryKey: ['client', 'site', siteId, 'visit-verifications', weekStart],
    queryFn: async () => {
      const r = await clientApi.getVisitVerifications({
        site: siteId,
        occurrence_date__gte: weekStart,
        occurrence_date__lte: weekEnd,
      });
      return r.data.results ?? [];
    },
    enabled: !!coverage?.patrol_plan,
  });

  const plan = coverage?.patrol_plan;
  if (!plan) return null;

  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Visit Verification</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          What actually happened vs. what the plan expected, for the week of{' '}
          {weekStart}. Refreshed after each scoring cycle.
        </p>
      </div>
      <VerificationGrid
        weekStart={weekStart}
        visits={plan.required_visits}
        verifications={verifData ?? []}
      />
    </div>
  );
}


export default function ClientSiteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [assignError, setAssignError] = useState<string | null>(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [showContextForm, setShowContextForm] = useState(false);

  const { data: site, isLoading, isError } = useQuery({
    queryKey: ['client', 'sites', id],
    queryFn: () => clientApi.getSite(id!).then((r) => r.data),
    enabled: !!id,
  });

  const { data: scoreHistory } = useQuery({
    queryKey: ['client', 'site-score-history', id],
    queryFn: () => scoringApi.getClientScoreHistory('site', id!),
    enabled: !!id,
  });

  const { data: ruleTriggers } = useQuery({
    queryKey: ['client', 'site-rule-triggers', id],
    queryFn: () => scoringApi.getClientRuleTriggers('site', id!),
    enabled: !!id,
  });

  // Load available vendors for assignment
  const { data: vendorsData } = useQuery({
    queryKey: ['client', 'vendors', 'all'],
    queryFn: () => clientApi.getVendors({ page: '1' }).then((r) => r.data),
    enabled: showAssignForm,
  });

  const assignMutation = useMutation({
    mutationFn: (vendorOrgId: string) => clientApi.assignVendor(id!, vendorOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', 'sites'] });
      setShowAssignForm(false);
      setAssignError(null);
      setSelectedVendorId('');
    },
    onError: (err: unknown) => {
      setAssignError(extractApiError(err));
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">Failed to load site. Please try again later.</p>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-slate-500">Site not found</p>
      </div>
    );
  }

  const hasVendor = !!site.vendor_org;
  const vendors: VendorInfo[] = vendorsData?.results ?? [];
  const snapshots = scoreHistory?.results ?? [];
  const previousScore = snapshots.length >= 2
    ? Number(snapshots[snapshots.length - 2].score_value)
    : null;

  return (
    <>
      <PageHeader
        title={site.name}
        description={`${site.address}, ${site.city}, ${site.state} ${site.zip_code}`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/client/sites/${id}/edit`)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
            >
              Edit Site
            </button>
            <button
              onClick={() => navigate('/client/sites')}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
            >
              Back to Sites
            </button>
          </div>
        }
      />

      {/* Pending vendor suggestions for the Site Context */}
      <ContextSuggestionsCard siteId={site.id} />

      {/* Site Context Card */}
      {(() => {
        const ctx = site.context;
        const ctxComplete = !!ctx?.completed;
        return (
          <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Site Context</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tells the scoring engine what kind of site this is. Required before assigning a vendor.
                </p>
              </div>
              {!showContextForm && (
                <button
                  onClick={() => setShowContextForm(true)}
                  className="text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  {ctxComplete ? 'Edit' : 'Complete now'}
                </button>
              )}
            </div>

            {!ctxComplete && !showContextForm && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Site Context Card is incomplete. You must complete it before you can assign a vendor.
              </div>
            )}

            {ctxComplete && !showContextForm && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Site Type</p>
                  <p className="text-slate-900 capitalize">{ctx?.site_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Coverage</p>
                  <p className="text-slate-900 capitalize">{ctx?.coverage_type}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Hours</p>
                  <p className="text-slate-900">{ctx?.coverage_hours}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Intensity</p>
                  <p className="text-slate-900 capitalize">{ctx?.patrol_intensity}</p>
                </div>
              </div>
            )}

            {showContextForm && (
              <div className="mt-3">
                <SiteContextForm
                  siteId={id!}
                  initialContext={ctx}
                  onSuccess={() => setShowContextForm(false)}
                  onCancel={() => setShowContextForm(false)}
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* Patrol Plan section — only for mobile_patrol service type */}
      {site.context?.service_type === 'mobile_patrol' && (
        <>
          <ClientPatrolPlanSection siteId={id!} />
          <ClientVerificationGridSection siteId={id!} />
        </>
      )}

      {/* Vendor Assignment */}
      {!hasVendor && !showAssignForm && (
        <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-blue-800">No Vendor Assigned</p>
            <p className="text-xs text-blue-600 mt-1">
              {site.context?.completed
                ? 'Assign a security vendor to start receiving performance reports.'
                : 'Complete the Site Context Card above before assigning a vendor.'}
            </p>
          </div>
          <button
            onClick={() => {
              if (!site.context?.completed) {
                setShowContextForm(true);
                return;
              }
              setShowAssignForm(true);
            }}
            disabled={!site.context?.completed}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Assign Vendor
          </button>
        </div>
      )}

      {hasVendor && !showAssignForm && (
        <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Current Vendor: {site.vendor_org_name}</p>
            <p className="text-xs text-slate-500 mt-1">You can reassign a different vendor to this site.</p>
          </div>
          <button
            onClick={() => setShowAssignForm(true)}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Change Vendor
          </button>
        </div>
      )}

      {showAssignForm && (
        <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">{hasVendor ? 'Change Vendor' : 'Assign Vendor'}</h3>
          {assignError && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{assignError}</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Select a vendor...</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name} (Score: {toGGScore(v.score)})</option>
              ))}
            </select>
            <button
              onClick={() => selectedVendorId && assignMutation.mutate(selectedVendorId)}
              disabled={!selectedVendorId || assignMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
            >
              {assignMutation.isPending ? 'Assigning...' : 'Confirm'}
            </button>
            <button
              onClick={() => { setShowAssignForm(false); setAssignError(null); }}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Info + Score row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Site Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Vendor</dt>
              <dd className="mt-1 text-sm text-slate-900">
                {site.vendor_org_name ? (
                  <button
                    onClick={() => site.vendor_org && navigate(`/client/vendors/${site.vendor_org}`)}
                    className="text-brand-600 hover:underline"
                  >
                    {site.vendor_org_name}
                  </button>
                ) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge label={site.vendor_assignment_status} variant={statusVariant(site.vendor_assignment_status)} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Address</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.address}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">City / State</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.city}, {site.state} {site.zip_code}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Timezone</dt>
              <dd className="mt-1 text-sm text-slate-900">{site.timezone}</dd>
            </div>
            {site.ingestion_email && (
              <div>
                <dt className="text-xs font-medium text-slate-400 uppercase">Ingestion Email</dt>
                <dd className="mt-1 text-sm text-slate-900 break-all">{site.ingestion_email}</dd>
              </div>
            )}
          </dl>
        </div>
        <ScoreCard label="Site Score" score={site.score} previousScore={previousScore} size="lg" />
      </div>

      {/* Score Trend Chart */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Score Trend</h2>
        <ScoreTrendChart snapshots={snapshots} />
      </div>

      {/* Rule Triggers */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <RuleTriggerList
          triggers={ruleTriggers?.results ?? []}
          title="Recent Rule Triggers"
        />
      </div>

      {/* Assigned Guards */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Assigned Guards ({site.assigned_guards?.length ?? 0})
        </h2>
        {site.assigned_guards && site.assigned_guards.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Assigned Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {site.assigned_guards.map((guard) => (
                  <tr key={guard.id}>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{guard.name}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`font-semibold ${getScoreColorClass(getScoreColor(guard.score))}`}>
                        {toGGScore(guard.score)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(guard.assigned_date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No guards assigned to this site yet.</p>
        )}
      </div>
    </>
  );
}
